import {
  IHicapsConnectEdge,
  IHicapsConnectMerchant,
  IHicapsConnectTerminal,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  IReffable,
  isSameRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';

const v1NameSeparator = ' - TID:';

/**
 * Owns the HICAPS merchant (provider) concept: what a merchant is called, when
 * it may be offered, and how one is derived from the V1 terminal rows that
 * predate it. Merchants are read on every payment and every settings screen, so
 * these rules must not be restated per surface.
 */
export class HicapsConnectMerchant {
  /**
   * The name a merchant is listed under: the user's nickname when they have set
   * one, otherwise the provider name HICAPS reports. `||` rather than `??`
   * because clearing the nickname leaves an empty string, which must still fall
   * back to the provider name instead of sorting above every named merchant.
   *
   * A terminal can carry a merchant id that has no merchant document yet, so
   * callers that only hold the id pass it as the fallback.
   */
  static displayName(
    merchant: Pick<IHicapsConnectMerchant, 'name' | 'providerName'> | undefined,
    fallback: string = ''
  ): string {
    if (!merchant) {
      return fallback;
    }
    return merchant.name || merchant.providerName;
  }

  /**
   * A merchant edge is only a mapping of which merchants a terminal carries, so
   * staleness is recorded for visibility but must never withhold the merchant.
   * Only the user-owned `disabled` flag can do that.
   */
  static isEdgeActive(
    edge: IHicapsConnectEdge<IHicapsConnectMerchant>
  ): boolean {
    return !edge.disabled;
  }

  /** Whether a merchant may be offered for an invoice. */
  static isAvailableToPractitioners(
    merchant:
      | Pick<IHicapsConnectMerchant, 'disabled' | 'practitionerRef'>
      | undefined,
    practitionersOnInvoice: IReffable<IStaffer>[]
  ): boolean {
    if (merchant?.disabled) {
      return false;
    }

    const practitionerRef = merchant?.practitionerRef;

    if (!practitionerRef) {
      return true;
    }

    return practitionersOnInvoice.some((practitionerOnInvoice) =>
      isSameRef(practitionerOnInvoice, practitionerRef)
    );
  }

  /** V1 names are `"{providerName} - TID:{serverUrl}"`; falls back to the raw name. */
  static extractProviderNameFromV1(v1Name: string): string {
    const separatorIndex = v1Name.indexOf(v1NameSeparator);
    return separatorIndex > 0 ? v1Name.slice(0, separatorIndex) : v1Name;
  }

  /**
   * Prefers a V1 row still carrying the generated name, because a row the user
   * renamed no longer says anything about the provider. With nothing parseable
   * left, the merchant id is a truthful label where a guess would not be.
   */
  static resolveProviderNameFromV1(
    merchantId: string,
    terminals: Pick<IHicapsConnectTerminal, 'name'>[]
  ): string {
    const parseableTerminal = terminals.find((terminal) =>
      terminal.name.includes(v1NameSeparator)
    );
    const sourceName = parseableTerminal?.name ?? first(terminals)?.name;
    return sourceName
      ? HicapsConnectMerchant.extractProviderNameFromV1(sourceName)
      : merchantId;
  }

  /**
   * A merchant restricted to a practitioner is hidden from every invoice that
   * practitioner is not on, so a partially assigned group must not impose a
   * restriction.
   */
  static resolvePractitionerRef(
    terminals: Pick<IHicapsConnectTerminal, 'practitionerRef'>[]
  ): DocumentReference<IStaffer> | undefined {
    const assignedRefs = terminals
      .map((terminal) => terminal.practitionerRef)
      .filter((ref): ref is DocumentReference<IStaffer> => !!ref);

    const everyTerminalAssigned = assignedRefs.length === terminals.length;
    if (!everyTerminalAssigned) {
      return;
    }

    const uniquePaths = new Set(assignedRefs.map((ref) => ref.path));
    return uniquePaths.size === 1 ? first(assignedRefs) : undefined;
  }
}
